import { zodResolver } from "@hookform/resolvers/zod";
import { Save, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { EnumSelect } from "@/components/shared/enum-select";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useActiveBuInstances } from "@/hooks/use-bu-instances";
import { MASTER_DATA_ENTITY_TYPE_OPTIONS, type MasterDataEntityType } from "@/lib/enums";
import {
  type MasterDataMappingFormValues,
  masterDataMappingFormSchema,
} from "@/schemas/master-data-mappings";
import { MasterDataValueSelect } from "./MasterDataValueSelect";

// Not a persisted enum column (DB stores a plain boolean), so this stays a
// local UI mapping rather than a src/lib/enums.ts entry — mirrors
// BuInstanceForm.tsx's ACTIVE_OPTIONS.
const ACTIVE_OPTIONS = [
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

interface MasterDataMappingFormDefaults {
  sourceBuId?: string;
  targetBuId?: string;
  sourceRefId?: string;
  targetRefId?: string | null;
  isActive?: boolean;
}

interface MasterDataMappingFormProps {
  /** Fixed by the active tab — not user-editable (see MasterDataMappingsLayout). */
  entityType: MasterDataEntityType;
  defaultValues?: MasterDataMappingFormDefaults;
  onSubmit: (values: MasterDataMappingFormValues) => void;
  onCancel: () => void;
  isSaving: boolean;
  submitLabel?: string;
}

/**
 * One reusable form drives both create and edit — mirrors
 * `BuInstanceForm.tsx`. Source/Target reference ID use
 * `MasterDataValueSelect` (a searchable-plus-free-text combobox over the
 * extracted-value cache for whichever BU is currently selected), not a bare
 * `<Input>` — it re-scopes reactively as `sourceBuId`/`targetBuId` change.
 */
export function MasterDataMappingForm({
  entityType,
  defaultValues,
  onSubmit,
  onCancel,
  isSaving,
  submitLabel = "Save",
}: MasterDataMappingFormProps) {
  const { data: activeBuInstances = [] } = useActiveBuInstances();
  const buOptions = activeBuInstances.map((bu) => ({ value: bu.id, label: bu.name }));

  const form = useForm<MasterDataMappingFormValues>({
    resolver: zodResolver(masterDataMappingFormSchema),
    defaultValues: {
      entityType,
      sourceBuId: defaultValues?.sourceBuId ?? "",
      targetBuId: defaultValues?.targetBuId ?? "",
      sourceRefId: defaultValues?.sourceRefId ?? "",
      targetRefId: defaultValues?.targetRefId ?? "",
      isActive: defaultValues?.isActive ?? true,
    },
  });

  const sourceBuId = form.watch("sourceBuId");
  const targetBuId = form.watch("targetBuId");
  const entityTypeLabel =
    MASTER_DATA_ENTITY_TYPE_OPTIONS.find((option) => option.value === entityType)?.label ??
    entityType;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Entity type: <span className="font-medium text-foreground">{entityTypeLabel}</span>
        </p>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="sourceBuId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Source BU</FormLabel>
                <FormControl>
                  <EnumSelect
                    value={field.value}
                    onValueChange={field.onChange}
                    options={buOptions}
                    placeholder="Select a BU"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="targetBuId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Target BU</FormLabel>
                <FormControl>
                  <EnumSelect
                    value={field.value}
                    onValueChange={field.onChange}
                    options={buOptions}
                    placeholder="Select a BU"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="sourceRefId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Source reference ID</FormLabel>
                <FormControl>
                  <MasterDataValueSelect
                    value={field.value || null}
                    onChange={field.onChange}
                    buInstanceId={sourceBuId || null}
                    entityType={entityType}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="targetRefId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Target reference ID</FormLabel>
                <FormControl>
                  <MasterDataValueSelect
                    value={field.value || null}
                    onChange={field.onChange}
                    buInstanceId={targetBuId || null}
                    entityType={entityType}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="isActive"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <FormControl>
                <EnumSelect
                  value={field.value ? "true" : "false"}
                  onValueChange={(value) => field.onChange(value === "true")}
                  options={ACTIVE_OPTIONS}
                  className="w-[180px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isSaving}>
            <Save className="h-4 w-4 mr-1" />
            {isSaving ? "Saving…" : submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
